import { Lock, Trash2, UserMinus, Users, X } from 'lucide-react';
import { useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SelectedPanelProps, SelectedEmployee } from './types';
import { cn } from '@/lib/utils';

// ============================================================================
// Utils
// ============================================================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

// ============================================================================
// Component
// ============================================================================

/**
 * Panel showing currently selected employees.
 * Shows actual people, not abstract departments.
 */
export function SelectedPanel({
  selection,
  onRemoveEmployee,
  onClearAll,
  onForceRemoveLocked,
  disabled = false,
}: Omit<SelectedPanelProps, 'onRemoveDepartment'>) {
  const totalCount = selection.employees.length;
  const removableCount = selection.employees.filter((e) => !e.locked).length;
  const hasSelection = totalCount > 0;
  const hasRemovable = removableCount > 0;

  return (
    <div className="flex flex-col h-full border rounded-xl bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm">Selected</h4>
          {totalCount > 0 && (
            <Badge variant="default" className="h-5 px-1.5 text-xs">
              {totalCount}
            </Badge>
          )}
        </div>
        {hasRemovable && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            disabled={disabled}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Clear
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 min-h-0">
        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
              <Users className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">No one selected</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Select employees from the list
            </p>
          </div>
        ) : (
          <div className="p-1">
            {selection.employees.map((employee) => (
              <EmployeeItem
                key={employee.id}
                employee={employee}
                onRemove={() => onRemoveEmployee(employee.id)}
                onForceRemoveLocked={
                  onForceRemoveLocked
                    ? () => onForceRemoveLocked(employee.id)
                    : undefined
                }
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {hasSelection && (
        <div className="px-3 py-2 border-t bg-muted/20">
          <p className="text-xs text-muted-foreground text-center">
            {totalCount} {totalCount === 1 ? 'employee' : 'employees'} will be assigned
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EmployeeItem Component
// ============================================================================

interface EmployeeItemProps {
  employee: SelectedEmployee;
  onRemove: () => void;
  /**
   * Optional async force-exclude handler for locked rows. When provided, a
   * UserMinus icon button appears on hover next to the lock badge, calling
   * this handler. The handler is expected to persist immediately (the
   * backend's per-employee `excluded=true` override) and to refetch
   * assignments so the row disappears from the list. While the handler is
   * in flight the button is disabled to prevent double-clicks.
   */
  onForceRemoveLocked?: () => Promise<void> | void;
  disabled?: boolean;
}

function EmployeeItem({
  employee,
  onRemove,
  onForceRemoveLocked,
  disabled,
}: EmployeeItemProps) {
  const formatName = useEmployeeNameFormatter();
  const displayName = formatName(employee);
  const isLocked = employee.locked === true;
  const [isExcluding, setIsExcluding] = useState(false);

  const handleExclude = async () => {
    if (!onForceRemoveLocked || isExcluding) return;
    setIsExcluding(true);
    try {
      await onForceRemoveLocked();
    } finally {
      setIsExcluding(false);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-2 py-2 rounded-lg transition-colors group',
        'hover:bg-muted/50'
      )}
      title={isLocked && employee.lockedReason ? `Auto-included ${employee.lockedReason}` : undefined}
    >
      <Avatar className="h-8 w-8">
        {employee.avatarUrl && <AvatarImage src={employee.avatarUrl} />}
        <AvatarFallback className="text-xs font-medium">
          {getInitials(employee.firstName, employee.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="text-sm font-medium truncate">{displayName}</div>
          {isLocked && employee.lockedReason && (
            <Badge
              variant="outline"
              className="h-4 px-1 text-[10px] font-normal text-muted-foreground gap-1"
            >
              <Lock className="h-2.5 w-2.5" />
              {employee.lockedReason}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {employee.position?.title ?? 'No position'}
          {employee.department?.name && ` · ${employee.department.name}`}
        </div>
      </div>
      {/* Locked rows expose a force-exclude icon only when the parent
          provided an `onForceRemoveLocked` handler (Assignments tab on the
          Absence Type configurator does). For other consumers of the
          picker, locked rows stay un-removable. */}
      {isLocked && onForceRemoveLocked ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                onClick={handleExclude}
                disabled={disabled || isExcluding}
              >
                <UserMinus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[260px]">
              Exclude only from this absence type
              {employee.lockedReason && (
                <>
                  {' '}
                  — employee stays{' '}
                  {/* strip the "via " prefix from `lockedReason` so the
                      sentence reads naturally: "stays in Contractor (in house)"
                      instead of "stays via Contractor (in house)" */}
                  in {employee.lockedReason.replace(/^via\s+/, '')}
                </>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : !isLocked ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          disabled={disabled}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
